import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import AnimatedImage from "@/components/AnimatedImage";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import foundersPhoto from "@/assets/aquadias-founders.webp";
import brustablettFizz from "@/assets/brustablett-fizz.webp";

const values = [
  { title: "Hälsa", desc: "Vi brinner för att göra hälsa tillgängligt och enkelt i vardagen." },
  { title: "Enkelhet", desc: "Vår lösning är designad för att vara så enkel som möjligt att använda." },
  { title: "Omtanke", desc: "Vi bryr oss om våra kunder, deras medarbetare och vår planet." },
  { title: "Innovation", desc: "Vi utvecklar ständigt nya produkter och förbättrar befintliga." },
];

const RevealDiv = ({
  children,
  delay = 0,
  fromRight = false,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  fromRight?: boolean;
  className?: string;
}) => {
  const { ref, className: revealClass } = useReveal({ fromRight });
  return (
    <div ref={ref} className={`${revealClass} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const FadeUp = ({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`fade-up ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const OmOssPage = () => {
  return (
    <Layout>
      <Seo
        title="Om aQuadias – från idé till friskare arbetsplatser"
        description="Vår vision, våra kärnvärden och vägen som tagit aQuadias till kontor och arbetsplatser över hela Sverige."
        path="/om-oss"
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-coral-500/20 via-cream-100 to-cream-50 pt-20 md:pt-28 pb-12 px-6 text-center">
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight text-foreground mb-4">
          Om aQuadias
        </h1>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Från en enkel idé till en friskare vardag på arbetsplatser runt om i Sverige.
        </p>
      </section>

      {/* Story */}
      <section className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <RevealDiv className="aspect-[4/5] w-full">
            <AnimatedImage src={foundersPhoto} alt="Grundarna av aQuadias" className="shadow-lg" fadeOnly fill />
          </RevealDiv>
          <RevealDiv fromRight delay={150} className="space-y-6">
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-foreground">FRÅN IDÉ TILL VERKLIGHET</h2>
            <p className="text-muted-foreground leading-relaxed">
              aQuadias grundades med en enkel vision: att göra det lättare för människor att ta hand om sin hälsa, direkt på arbetsplatsen. Vi såg att de flesta ville äta bättre och må bättre, men att steget ofta var för stort.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Vår lösning? En snygg dispenser med brustabletter i naturliga smaker som gör det kul och enkelt att dricka vitaminer under arbetsdagen. Idag finns vi på kontor, i restauranger och väntrum över hela Sverige.
            </p>
          </RevealDiv>
        </div>
      </section>

      {/* Values */}
      <section className="bg-cream-50 px-6 md:px-12 py-20 md:py-28">
        <FadeUp className="max-w-5xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-foreground mb-4">Våra kärnvärden</h2>
        </FadeUp>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v, i) => {
            const gradients = [
              "from-coral-400/20 to-cream-100",
              "from-terracotta-100 to-cream-100",
              "from-coral-400/20 to-cream-100",
              "from-terracotta-100 to-cream-100",
            ];
            return (
              <FadeUp key={v.title} delay={i * 120}>
                <div className={`bg-gradient-to-br ${gradients[i % 4]} rounded-2xl p-8 space-y-3`}>
                  <h3 className="text-lg font-bold uppercase tracking-wider text-foreground">{v.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* Vision */}
      <section className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <RevealDiv className="space-y-6">
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-foreground">VÅR VISION</h2>
            <p className="text-muted-foreground leading-relaxed">
              Vi vill göra Sverige friskare, en arbetsplats i taget. Genom att placera hälsa mitt i vardagen tror vi att fler väljer att ta hand om sig själva.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex bg-terracotta-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-terracotta-600 transition-all duration-300"
            >
              Kontakta oss →
            </Link>
          </RevealDiv>
          <RevealDiv fromRight delay={150} className="aspect-[4/5] w-full">
            <AnimatedImage src={brustablettFizz} alt="Brustablett som löses upp i vatten" className="shadow-lg" fill />
          </RevealDiv>
        </div>
      </section>
    </Layout>
  );
};

export default OmOssPage;
